import { type PropsWithChildren, useEffect, useState } from 'react';

import { Backdrop, Modal as MuiModal, useTheme } from '@mui/material';
import styled from 'styled-components';

import Animator from './animation/Animator';

export enum ModalSize {
    Large,
    Medium,
    Small
}

const StyledBackdrop = styled(Backdrop)`
    z-index: -1;
`;

const ContentContainer = styled.div<{ noPadding; size }>`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    transform-origin: 0 0;
    max-width: ${(props) => {
        if (props.size === ModalSize.Large) return '750px';
        if (props.size === ModalSize.Medium) return '500px';
        if (props.size === ModalSize.Small) return '400px';
        return '500px';
    }};
    margin: auto;
    background-color: ${(props) => props.theme.colors.white};
    padding: ${(props) => (props.noPadding ? '0' : '2rem')};
    border-radius: 4px;

    max-height: 95vh;
    overflow-y: auto;

    /**
     * Transitioned Styles
     */
    opacity: 0;
    box-shadow: none;
    transform: scale(0.75) translate(-50%, -50%);
`;

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    size?: ModalSize;
    noPadding?: boolean;
}

const Modal = (props: PropsWithChildren<ModalProps>) => {
    const { isOpen, onClose, noPadding, size, children } = props;
    const [open, setOpen] = useState(isOpen);
    const theme = useTheme();

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        onClose();
    };

    const duration = 220;

    const targetStyles = {
        opacity: 1,
        boxShadow: theme.shadows[24],
        transform: 'scale(1) translate(-50%, -50%)'
    };

    const transition = {
        entering: targetStyles,
        entered: targetStyles
    };

    return (
        <MuiModal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: StyledBackdrop }}
            slotProps={{ backdrop: { transitionDuration: duration } }}
        >
            <Animator in={open} transitionStyles={transition} timeout={duration}>
                <ContentContainer noPadding={noPadding} size={size}>
                    {children}
                </ContentContainer>
            </Animator>
        </MuiModal>
    );
};

export default Modal;
